<template>
  <section class="hong-kong-market">
    <a-menu v-model="board" class="board-tabs" mode="horizontal">
      <a-menu-item key="overview">行情</a-menu-item>
      <a-menu-item key="main">港股主板</a-menu-item>
      <a-menu-item key="gem">创业板</a-menu-item>
    </a-menu>

    <HongKongBoardTable v-for="category in ['main', 'gem']" :key="category" v-show="selectedBoard === category" :active="selectedBoard === category" :board="category" @select-stock="openTrade" />
    <div v-if="error && selectedBoard === 'overview'" role="alert">{{ error }}</div>
    <div v-show="selectedBoard === 'overview'" class="hk-overview">
      <div class="market-summary">
        <section class="market-panel index-panel">
          <HongKongMinuteChart :active="selectedBoard === 'overview'" :indexes="indexes" />
        </section>

        <HongKongMarketBreadth :loading="loading && !diagram.length" :diagram="diagram" :ratio="ratio" />
        <HongKongIndustryPanel :active="selectedBoard === 'overview'" @select-stock="openTrade" />
      </div>

      <div class="rank-title"><span>{{ rankTitle }}</span><div v-if="rankTabs.length > 3"><button type="button" aria-label="上一组排行" @click="scrollRanks(-1)">‹</button><button type="button" aria-label="下一组排行" @click="scrollRanks(1)">›</button></div></div>
      <div ref="rankGrid" class="rank-grid" tabindex="0" aria-label="港股今日排行，可左右滚动">
        <section v-for="tab in rankTabs" :key="tab.key" class="rank-panel">
          <TitleHeader :title="tab.name" />
          <div class="rank-body" v-loading="loading && !(rank[tab.key] || []).length" element-loading-background="var(--theme-bg)">
            <div class="rank-header"><span>名称</span><span>最新价</span><span>涨跌幅</span></div>
            <div v-for="item in sharedStockRows(rank[tab.key], 'hk')" :key="item.code || item.symbol" class="rank-row" role="button" tabindex="0" @click="openTrade(item)" @keydown.enter="openTrade(item)">
              <span>{{ item.name || item.prod_name || '--' }}</span>
              <span>{{ price(item.last_px == null || item.last_px === '' ? item.nzg_zxj : item.last_px) }}</span>
              <span :class="changeClass(item)">{{ percent(item.px_change_rate == null ? item.nzg_zdf : item.px_change_rate) }}</span>
            </div>
            <div v-if="!(rank[tab.key] || []).length" class="empty">暂无排行数据</div>
          </div>
        </section>
      </div>
    </div>
    <HongKongTradePanel v-if="tradeStock" :key="tradeStock.code" :stock="tradeStock" @close="tradeStock = null" />
  </section>
</template>

<script>
import { quotePercent, quotePrice } from '@/utils/quoteDisplay'
import { sharedStockRows } from '@/utils/stockQuoteFeed'
import { getHongKongMarketSummary, getHongKongRank } from './api'
import { pollMarket } from '@/utils/marketRefresh'
import HongKongMinuteChart from './components/HongKongMinuteChart.vue'
import HongKongMarketBreadth from './components/HongKongMarketBreadth.vue'
import HongKongIndustryPanel from './components/HongKongIndustryPanel.vue'
import TitleHeader from '../home/components/titleHeader.vue'
import HongKongBoardTable from './components/HongKongBoardTable.vue'
import HongKongTradePanel from './components/HongKongTradePanel.vue'

import rankNavigation from './rankNavigation'
export default {
  mixins: [rankNavigation],
  name: 'HongKongMarket',
  components: { HongKongMinuteChart, HongKongMarketBreadth, HongKongIndustryPanel, HongKongBoardTable, HongKongTradePanel, TitleHeader },
  data() {
    return {
      loading: false,
      tradeStock: null,
      error: '',
      rank: {},
      today: {},
      board: ['overview'],
      allRankTabs: [
        { key: 'advance1', name: '主板涨幅榜' },
        { key: 'decline1', name: '主板跌幅榜' },
        { key: 'amount1', name: '主板成交额榜' },
        { key: 'advance2', name: '创业板涨幅榜' },
        { key: 'decline2', name: '创业板跌幅榜' },
        { key: 'amount2', name: '创业板成交额榜' }
      ]
    }
  },
  computed: {
    indexes() { return this.rank.index || [] },
    diagram() { return this.today.diagram || [] },
    ratio() { return this.today.ratio || {} },
    selectedBoard() { return this.board[0] },
    rankTabs() {
      if (this.selectedBoard === 'main') return this.allRankTabs.slice(0, 3)
      if (this.selectedBoard === 'gem') return this.allRankTabs.slice(3)
      return this.allRankTabs
    },
    rankTitle() {
      return { main: '港股主板排行', gem: '创业板排行' }[this.selectedBoard] || '今日排行'
    }
  },
  watch: { selectedBoard() { this.stopPolling(); if (this.selectedBoard === 'overview') this.activate() } },
  mounted() { this.activate() },
  beforeDestroy() { this.disposed = true; this.stopPolling() },
  methods: {
    sharedStockRows,
    price: quotePrice,
    stopPolling() { if (this.stopRefresh) this.stopRefresh(); this.stopRefresh = null },
    async activate() {
      if (this.disposed || this.selectedBoard !== 'overview' || this.stopRefresh) return
      try { await this.load() }
      finally {
        if (!this.disposed && this.selectedBoard === 'overview' && !this.stopRefresh) this.stopRefresh = pollMarket(() => this.load(true), 'hk')
      }
    },
    scrollRanks(direction) {
      const grid = this.$refs.rankGrid
      if (grid) grid.scrollBy({ left: direction * grid.clientWidth, behavior: 'smooth' })
    },
    openTrade(item) {
      const code = item.full_code || (/^hk/i.test(item.code || '') ? item.code : `hk${item.code}`)
      if (!/^hk\d{5}$/i.test(code)) return
      this.tradeStock = { code: code.toLowerCase(), name: item.prod_name || item.name || item.nzg_name || '' }
    },
    async load(refresh = false) {
      if (this.disposed || this.loading || this.selectedBoard !== 'overview') return
      refresh = refresh === true
      if (refresh && this.selectedBoard !== 'overview') return
      this.loading = true
      this.error = ''
      try {
        const rank = await getHongKongRank()
        if (this.disposed) return
        this.rank = (rank && (rank.data || rank)) || {}
        if (this.selectedBoard !== 'overview') return
        try {
          const today = await getHongKongMarketSummary()
          if (this.disposed) return
          this.today = (today && (today.data || today)) || {}
        } catch (error) {
          // Retain the last valid summary on refresh failure.
        }
      } catch (error) {
        if (!Object.keys(this.rank).length) this.error = '港股行情暂时无法加载，请稍后重试。'
      } finally {
        this.loading = false
      }
    },
    percent: quotePercent,
    changeClass(item, key = 'px_change_rate') {
      const value = Number(item[key])
      return value < 0 ? 'fall' : value > 0 ? 'rise' : 'flat'
    }
  }
}
</script>

<style scoped>
.hong-kong-market { min-width: 1100px; flex: 1; min-height: 0; display: flex; flex-direction: column; padding: 0; background: var(--theme-bg); color: var(--theme-text); }
.hk-overview { flex: 1; min-height: 0; display: flex; flex-direction: column; }
.market-summary, .rank-grid { display: grid; gap: 4px; }
.market-summary { grid-template-columns: repeat(3, minmax(0, 1fr)); flex-shrink: 0; height: max(38.6vh, calc(34.495vh + 36px)); }
.index-panel { min-width: 0; padding: 0; background: var(--theme-bg); }
.rank-row, .rank-header { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); align-items: center; width: 100%; height: 31px; padding: 0 11px; color: var(--theme-label); text-align: center; font-size: 14px; }
.rank-row span, .rank-header span { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.rank-row[role="button"] { cursor: pointer; }
.rank-title { display: flex; align-items: center; justify-content: space-between; flex: 0 0 35px; line-height: 35px; margin: 5px 0; padding: 0 8px; color: var(--theme-text); font-size: 14px; }
.rank-title button { width: 32px; height: 26px; margin-left: 6px; line-height: 20px; border: 1px solid var(--theme-border); border-radius: 3px; background: var(--theme-header); color: var(--theme-label); cursor: pointer; font-size: 22px; }
.rank-grid { flex: 1; min-height: 0; grid-auto-flow: column; grid-auto-columns: calc((100% - 30px) / 3); gap: 15px; overflow-x: auto; overflow-y: hidden; scroll-snap-type: x mandatory; scrollbar-width: thin; padding-bottom: 5px; }
.rank-panel { min-width: 0; min-height: 0; display: flex; flex-direction: column; scroll-snap-align: start; background: var(--theme-bg); }
.rank-body { flex: 1; min-height: 0; overflow-y: auto; scrollbar-width: thin; scrollbar-color: #c1c1c1 #eee; }
.rank-header { position: sticky; top: 0; z-index: 1; background: var(--theme-bg); }
.rank-body::-webkit-scrollbar { width: 6px; background: #eee; }
.rank-body::-webkit-scrollbar-thumb { background: #c1c1c1; border-radius: 8px; }
.rise { color: var(--quote-up); } .fall { color: var(--quote-down); } .flat { color: #9da6b3; }
.empty, .market-state { padding: 40px 0; color: #9da6b3; text-align: center; }
.market-state { min-height: 220px; }
</style>

<style>
.hong-kong-market .board-tabs {
  flex-shrink: 0;
  margin-bottom: 0;
  background: var(--theme-header);
  border-bottom: 0 !important;
}
.hong-kong-market .board-tabs .ant-menu-item {
  min-width: 120px;
  color: var(--theme-label);
  text-align: center;
}
.hong-kong-market .board-tabs .ant-menu-item-selected,
.hong-kong-market .board-tabs .ant-menu-item-active,
.hong-kong-market .board-tabs .ant-menu-item:hover {
  color: var(--theme-text) !important;
  border-bottom: 2px solid var(--theme-text) !important;
}
</style>
